import { useEffect, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Button, Icon } from "semantic-ui-react";

import "./CardUploadImagePackage.scss";
import { PackageImage } from "react-lib/apps/HISV3/HCU/sequence/SettingPackage";

type CardUploadImagePackageProps = {
  selectImage?: number;
  packageImage?: PackageImage[];
  fileInputRef: React.RefObject<HTMLInputElement>;

  onChange: (packageImage: PackageImage[]) => void;
};

export const CardUploadImagePackage = ({
  selectImage = 1,
  packageImage = [],
  fileInputRef,
  onChange,
}: CardUploadImagePackageProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>();

  /**
   * selectImage = 1,2,3,4
   *
   * packageImage index = 0,1,2,3
   */
  const currentIndex = selectImage - 1;

  /**
   * รูปปัจจุบันที่กำลังแก้ไข
   */
  const currentImage = packageImage[currentIndex];

  /**
   * รูปต้นฉบับ
   */
  const imageHero = currentImage?.image || "";

  /**
   * โหลด crop / zoom ของรูปที่เลือก
   */
  useEffect(() => {
    const currentImage = packageImage[currentIndex];
    const extra = currentImage?.extra;

    setCrop(
      extra?.imageHeroCrop || {
        x: 0,
        y: 0,
      },
    );

    setZoom(extra?.imageHeroZoom || 1);
    setCroppedAreaPixels(undefined);
  }, [packageImage, currentIndex]);

  /**
   * Update เฉพาะรูปที่กำลังเลือก
   */
  const updateCurrentImage = (update: Partial<PackageImage>) => {
    const updatedPackageImage = [...packageImage];

    /**
     * ถ้า array ยังไม่มี index นี้
     * สร้าง object ใหม่
     */
    updatedPackageImage[currentIndex] = {
      image: updatedPackageImage[currentIndex]?.image || "",
      thumbnail: updatedPackageImage[currentIndex]?.thumbnail || "",
      is_hero: currentIndex === 0,
      ...updatedPackageImage[currentIndex],
      ...update,
    };

    onChange(updatedPackageImage);
  };

  /**
   * Upload รูป
   */
  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const preview = URL.createObjectURL(file);

    console.log("FILE:", file);
    console.log("PREVIEW:", preview);

    const updatedPackageImage = [...packageImage];

    updatedPackageImage[currentIndex] = {
      ...(updatedPackageImage[currentIndex] || {}),
      active: true,
      image: preview,
      thumbnail: preview,
      is_hero: currentIndex === 0,
      extra: {
        imageHeroCrop: {
          x: 0,
          y: 0,
        },
        imageHeroZoom: 1,
      },
    };

    console.log("UPDATED:", updatedPackageImage);

    onChange(updatedPackageImage);

    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(undefined);
  };

  /**
   * Delete
   */
  const handleDelete = () => {
    const updatedPackageImage = [...packageImage];

    /**
     * ลบเฉพาะข้อมูลของรูปที่เลือก
     *
     * ไม่ splice เพราะต้องการรักษา
     * ตำแหน่งรูป 1-4
     */
    updatedPackageImage[currentIndex] = {
      ...(updatedPackageImage[currentIndex] || {}),
      active: false,
      image: "",
      thumbnail: "",
      is_hero: currentIndex === 0,
      extra: {
        imageHeroCrop: {
          x: 0,
          y: 0,
        },
        imageHeroZoom: 1,
      },
    };

    onChange(updatedPackageImage);

    setCrop({
      x: 0,
      y: 0,
    });

    setZoom(1);

    setCroppedAreaPixels(undefined);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Reset Crop
   *
   * กลับไปใช้ original image
   */
  const handleReset = () => {
    if (!currentImage?.image) return;

    updateCurrentImage({
      image: currentImage.image,
      thumbnail: currentImage.image,
      is_hero: currentIndex === 0,
      extra: {
        imageHeroCrop: {
          x: 0,
          y: 0,
        },
        imageHeroZoom: 1,
      },
    });

    setCrop({
      x: 0,
      y: 0,
    });

    setZoom(1);

    setCroppedAreaPixels(undefined);
  };

  /**
   * Save Crop
   */
  const handleSaveCrop = async () => {
    if (!imageHero || !croppedAreaPixels) return;

    const croppedImage = await getCroppedImg(imageHero, croppedAreaPixels);

    updateCurrentImage({
      image: imageHero,
      thumbnail: croppedImage,
      is_hero: currentIndex === 0,
      extra: {
        imageHeroCrop: crop,
        imageHeroZoom: zoom,
      },
    });
  };

  console.log("test_packageImage", packageImage);

  console.log("test_currentImage", currentImage);

  return (
    <div className="upload-image-package">
      <div className="upload-label">
        <div
          style={{
            fontSize: "1.15rem",
            marginRight: "10px",
          }}
        >
          รูปแพ็กเกจ
        </div>

        <div
          style={{
            fontSize: "12px",
            color: "#7B8792",
          }}
        >
          รูปที่ 1 ใช้เป็น Thumbnail บนการ์ด · รูปที่ 2–4 แสดงในหน้ารายละเอียดบนแอป
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            color: "rgb(194, 86, 111)",
          }}
        >
          {`กำลังแก้ไข: รูปที่ ${selectImage}/4`}
        </div>
      </div>

      <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={handleUpload} />

      {imageHero ? (
        <div className="crop-wrapper">
          <Cropper
            image={imageHero}
            crop={crop}
            zoom={zoom}
            aspect={4/3}
            cropShape="rect"
            showGrid={false}
            objectFit="vertical-cover"
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
          />

          <div className="crop-tip">✥ ลากเพื่อจัดตำแหน่ง · เลื่อนซูม</div>

          <div className="crop-toolbar">
            <Button
              size="small"
              style={{
                color: "var(--primary-theme-color-dark)",
                backgroundColor: "#fff",
              }}
              icon="refresh"
              content="เปลี่ยนรูป"
              onClick={() => fileInputRef.current?.click()}
            />

            <Button
              size="small"
              style={{
                backgroundColor: "#fff",
              }}
              icon="undo"
              title="รีเซ็ต"
              onClick={handleReset}
            />

            <Button
              size="small"
              style={{
                color: "green",
                backgroundColor: "#fff",
              }}
              icon="check"
              content="บันทึก"
              onClick={handleSaveCrop}
            />

            <Button
              size="small"
              style={{
                color: "red",
                backgroundColor: "#fff",
              }}
              icon="trash"
              title="ลบ"
              onClick={handleDelete}
            />
          </div>

          <div className="zoom-toolbar">
            <Button
              icon="minus"
              size="mini"
              disabled={zoom <= 1}
              onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
            />

            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />

            <Button
              icon="plus"
              size="mini"
              disabled={zoom >= 3}
              onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
            />

            <span>{Math.round(zoom * 100)}%</span>
          </div>
        </div>
      ) : (
        <div
          className="upload-box"
          style={{
            ...(selectImage === 0 && {
              cursor: "not-allowed",
            }),
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <Icon name="upload" size="huge" />

          <div className="upload-title">คลิกเพื่ออัปโหลดรูป</div>

          <div className="upload-desc">PNG / JPG</div>
        </div>
      )}
    </div>
  );
};

export async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  const image = new Image();

  image.src = imageSrc;

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("ไม่สามารถโหลดรูปภาพได้"));
  });

  const canvas = document.createElement("canvas");

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("ไม่สามารถสร้าง canvas context ได้");
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return canvas.toDataURL("image/jpeg", 0.95);
}
